import { breakpoints as customMedia } from '@fabric-ds/tailwind-config/colors.js';
import { useEffect, useState } from 'react';

// List of Fabric breakpoints found in customMedia
const VALID_BREAKPOINTS = [
    'lg',
    'max480',
    'max768',
    'max990',
    'md',
    'min480',
    'min768',
    'min990',
    'sm',
] as const;

type Breakpoint = typeof VALID_BREAKPOINTS[number];
type BreakpointMatches = { [Property in Breakpoint]: boolean };

const customMediaMap: { [key: string]: Breakpoint } = Object.fromEntries(
    VALID_BREAKPOINTS.map((bp) => [customMedia[`--${bp}`], bp]),
);

// Singleton holding media queries with event listeners for Fabric breakpoints
type BreakpointQueries = { [Property in Breakpoint]: MediaQueryList };
let breakpointQueries: BreakpointQueries;

function initBreakpointQueries(): void {
    breakpointQueries = Object.fromEntries(
        VALID_BREAKPOINTS.map((bp) => [
            bp,
            window.matchMedia(customMedia[`--${bp}`]),
        ]),
    ) as BreakpointQueries;
}

function initBreakpointMatches(
    selectedBreakpoints: Breakpoint[],
): BreakpointMatches {
    return Object.fromEntries(
        selectedBreakpoints.map((bp) => [bp, false]),
    ) as BreakpointMatches;
}

function updateBreakpointMatches(
    ev: MediaQueryListEvent,
    prevMatches: BreakpointMatches,
    selectedBreakpoints: Breakpoint[],
): BreakpointMatches {
    const media = customMediaMap[ev.media];
    if (
        selectedBreakpoints.includes(media) &&
        ev.matches !== prevMatches[media]
    ) {
        return {
            ...prevMatches,
            [media]: ev.matches,
        };
    } else {
        return prevMatches;
    }
}

function addBreakpointListeners(
    selectedBreakpoints: Breakpoint[],
    changeHandler: (ev: MediaQueryListEvent) => void,
) {
    Object.entries(breakpointQueries)
        .filter(([bp]) => selectedBreakpoints.includes(bp as Breakpoint))
        .forEach(([, query]) => {
            if (query.addEventListener) {
                query.addEventListener('change', changeHandler);
            } else {
                // Fallback to deprecated API for older Safari browsers (prior to v14)
                query.addListener(changeHandler);
            }
        });
}

function removeBreakpointListeners(
    selectedBreakpoints: Breakpoint[],
    changeHandler: (ev: MediaQueryListEvent) => void,
) {
    Object.entries(breakpointQueries)
        .filter(([bp]) => selectedBreakpoints.includes(bp as Breakpoint))
        .forEach(([, query]) => {
            if (query.removeEventListener) {
                query.removeEventListener('change', changeHandler);
            } else {
                // Fallback to deprecated API for older Safari browsers (prior to v14)
                query.removeListener(changeHandler);
            }
        });
}

function isArrayEquals(arr1: unknown[], arr2: unknown[]): boolean {
    return (
        arr1.length === arr2.length && arr1.every((val) => arr2.includes(val))
    );
}

export function useBreakpoints(...selectedBreakpoints: Breakpoint[]) {
    const [currentSelectedBreakpoints, setCurrentSelectedBreakpoints] =
        useState(selectedBreakpoints);

    if (!isArrayEquals(selectedBreakpoints, currentSelectedBreakpoints)) {
        setCurrentSelectedBreakpoints(selectedBreakpoints);
    }

    const [breakpointMatches, setBreakpointMatches] =
        useState<BreakpointMatches>(() =>
            initBreakpointMatches(selectedBreakpoints),
        );

    useEffect(() => {
        if (!breakpointQueries) {
            // Lazy init singleton holding media queries
            initBreakpointQueries();
        }

        setBreakpointMatches((prevMatches) => {
            const changedBreakpoints = Object.entries(prevMatches)
                .filter(
                    ([bp, match]) =>
                        match !== breakpointQueries[bp as Breakpoint].matches,
                )
                .map(([bp]) => [
                    bp,
                    breakpointQueries[bp as Breakpoint].matches,
                ]);

            if (changedBreakpoints.length > 0) {
                return {
                    ...prevMatches,
                    ...Object.fromEntries(changedBreakpoints),
                };
            } else {
                return prevMatches;
            }
        });

        function handleBreakpointChange(ev: MediaQueryListEvent) {
            setBreakpointMatches((prevMatches) =>
                updateBreakpointMatches(
                    ev,
                    prevMatches,
                    currentSelectedBreakpoints,
                ),
            );
        }

        addBreakpointListeners(
            currentSelectedBreakpoints,
            handleBreakpointChange,
        );

        return () => {
            removeBreakpointListeners(
                currentSelectedBreakpoints,
                handleBreakpointChange,
            );
        };
    }, [currentSelectedBreakpoints]);

    return breakpointMatches;
}
